import React, { useState, useEffect, useRef } from 'react';
import { setDoc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { dailyAnswersColl, dailyAnswerDoc, petDoc, streakDoc } from '../db';
import { queueNotification } from '../hooks/usePushNotification';
import { UserProfile, Screen } from '../App';
import { getDayQuestion } from '../utils/dailyQuestions';
import { recordActivity } from '../utils/activityFeed';

interface Props { user: UserProfile; navigate: (s: Screen) => void; backTo?: Screen; }

interface StreakState {
  count: number;
  lastUpdatedDate: string;
  longestStreak: number;
  broken: boolean;
}

interface DayData {
  [uid: string]: { answer: string; name: string };
}

interface DayEntry {
  date: string;
  question: string;
  answers: DayData;
}

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getYesterdayKey() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(dateStr: string) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function getRecentDays(total = 7) {
  return Array.from({ length: total }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (total - 1 - i));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
}

export default function DailyQuestion({ user, navigate, backTo = 'bond' }: Props) {
  const [allDays, setAllDays] = useState<DayEntry[]>([]);
  const [myAnswer, setMyAnswer] = useState('');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [streak, setStreak] = useState<StreakState | null>(null);
  const [streakBreakShown, setStreakBreakShown] = useState(false);
  const breakHandled = useRef(false);
  const today = getTodayKey();
  const todayQuestion = getDayQuestion(today);
  const partner = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';

  // Streak subscription
  useEffect(() => {
    return onSnapshot(streakDoc(user.coupleId), snap => {
      if (snap.exists()) {
        setStreak(snap.data() as StreakState);
      } else {
        setDoc(streakDoc(user.coupleId), {
          count: 0, lastUpdatedDate: '', longestStreak: 0, broken: false,
        });
      }
    });
  }, [user.coupleId]);

  // Break check — run once after streak first loads
  useEffect(() => {
    if (!streak || breakHandled.current) return;
    breakHandled.current = true;
    if (streak.count > 0 && streak.lastUpdatedDate < getYesterdayKey()) {
      setDoc(streakDoc(user.coupleId), {
        count: 0,
        lastUpdatedDate: streak.lastUpdatedDate,
        longestStreak: streak.longestStreak,
        broken: true,
      });
      updateDoc(petDoc(user.coupleId), { streakBroken: true }).catch(() => {});
      setStreakBreakShown(true);
      setTimeout(() => setStreakBreakShown(false), 6000);
    }
  }, [streak, user.coupleId]);

  // Streak increment — fires when both users have answered today
  useEffect(() => {
    if (!streak || streak.lastUpdatedDate === today) return;
    const todayEntry = allDays.find(d => d.date === today);
    if (!todayEntry || Object.keys(todayEntry.answers).length < 2) return;

    const yesterday = getYesterdayKey();
    const isConsecutive = streak.lastUpdatedDate === yesterday && streak.count > 0;
    const newCount = isConsecutive ? streak.count + 1 : 1;
    const newLongest = Math.max(newCount, streak.longestStreak);

    setDoc(streakDoc(user.coupleId), {
      count: newCount,
      lastUpdatedDate: today,
      longestStreak: newLongest,
      broken: false,
    });

    if (newCount % 7 === 0) {
      updateDoc(petDoc(user.coupleId), {
        streakMilestone: newCount,
        happiness: 100,
        lastPlayed: serverTimestamp(),
      }).catch(() => {});
    }
  }, [streak, allDays, today, user.coupleId]);

  useEffect(() => {
    return onSnapshot(dailyAnswersColl(user.coupleId), (snap) => {
      const entries: DayEntry[] = snap.docs.map(d => ({
        date: d.id,
        question: getDayQuestion(d.id),
        answers: d.data() as DayData,
      })).sort((a, b) => b.date.localeCompare(a.date));
      setAllDays(entries);

      const todayDoc = snap.docs.find(d => d.id === today);
      if (todayDoc?.data()[user.uid]) {
        setMyAnswer(todayDoc.data()[user.uid].answer);
        setSaved(true);
      }
    });
  }, [today, user.uid, user.coupleId]);

  const save = async () => {
    if (!myAnswer.trim()) return;
    setLoading(true);
    await setDoc(dailyAnswerDoc(user.coupleId, today), {
      [user.uid]: { answer: myAnswer.trim(), name: user.displayName }
    }, { merge: true });
    recordActivity(user, {
      type: 'question',
      title: 'Günün sorusu cevaplandı',
      body: myAnswer.trim().slice(0, 140),
      dayKey: today,
      sourceId: user.uid,
    }).catch(() => {});
    setSaved(true);
    setLoading(false);
    // Notify partner
    if (user.partnerUid) {
      queueNotification(
        user.coupleId,
        user.partnerUid,
        `${user.displayName} bugünün sorusuna iz bıraktı`,
        'Sen de yazınca cevaplar yan yana açılacak.',
        'daily-question',
      );
    }
  };

  const todayEntry = allDays.find(d => d.date === today);
  const partnerAnsweredToday = todayEntry && Object.entries(todayEntry.answers).some(([uid]) => uid !== user.uid);
  const partnerEntryToday = todayEntry ? Object.entries(todayEntry.answers).find(([uid]) => uid !== user.uid)?.[1] : undefined;
  const canSeePartnerAnswer = saved && Boolean(partnerEntryToday);
  const bothAnsweredToday = saved && Boolean(partnerAnsweredToday);
  const rhythmCount = streak?.count ?? 0;
  const rhythmDisplay = rhythmCount > 0 ? `${rhythmCount} gün` : bothAnsweredToday ? 'Bugün eklendi' : 'Başlayacak';
  const rhythmText = bothAnsweredToday
    ? 'Bugün de ritme eklendi.'
    : saved
      ? `${partner} yazınca bugün de eklenir.`
      : partnerAnsweredToday
        ? 'Sen de yazınca bugün de eklenir.'
        : 'İkiniz de yazınca bugün ritme eklenir.';
  const dayMap = new Map(allDays.map(day => [day.date, day]));
  const recentRhythm = getRecentDays().map(date => {
    const entry = dayMap.get(date);
    const answerCount = entry ? Object.keys(entry.answers).length : 0;
    return { date, both: answerCount >= 2, one: answerCount === 1 };
  });

  // When both have answered today, make pet play + show confetti
  useEffect(() => {
    if (!saved || !partnerAnsweredToday) return;
    updateDoc(petDoc(user.coupleId), { happiness: 100, lastPlayed: new Date(), lastActive: new Date() }).catch(() => {});
  }, [saved, partnerAnsweredToday, user.coupleId]);

  const pastDays = allDays.filter(d => d.date !== today);

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Günün Sorusu</h1>
      </div>

      <div style={styles.body}>
        <AnimatePresence>
          {streakBreakShown && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={styles.breakBanner}
            >
              <span style={styles.breakIcon}>!</span>
              <div style={{ flex: 1 }}>
                <p style={styles.breakTitle}>Ritim durdu</p>
                <p style={styles.breakSub}>Bugün ikiniz de yazınca yeniden başlar.</p>
              </div>
              <button onClick={() => setStreakBreakShown(false)} style={styles.breakClose}>×</button>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 90, damping: 18 }}
          style={styles.questionCard}
        >
          <div style={styles.questionMeta}>
            <span>{formatDate(today)}</span>
          </div>
          <p style={styles.questionText}>{todayQuestion}</p>
        </motion.section>

        {!saved ? (
          <section style={styles.composerCard}>
            <p style={styles.cardLabel}>Senin cevabın</p>
            <textarea
              placeholder="Buraya yaz..."
              value={myAnswer}
              onChange={e => setMyAnswer(e.target.value)}
              rows={5}
              style={styles.textarea}
            />
            <motion.button
              whileTap={{ scale: 0.96 }}
              className="btn btn-primary"
              onClick={save}
              disabled={loading}
            >
              {loading ? 'Kaydediliyor...' : 'Cevabımı bırak'}
            </motion.button>
          </section>
        ) : (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={styles.answerCard}
          >
            <div style={styles.cardTop}>
              <p style={styles.cardLabel}>Senin cevabın</p>
              <button onClick={() => setSaved(false)} style={styles.editBtn}>Düzenle</button>
            </div>
            <p style={styles.answerText}>{myAnswer}</p>
          </motion.section>
        )}

        <motion.section
          style={canSeePartnerAnswer ? styles.partnerAnswerCard : styles.answerCard}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <div style={styles.cardTop}>
            <p style={styles.cardLabel}>{partner}'ın cevabı</p>
          </div>
          <AnimatePresence mode="wait">
            {canSeePartnerAnswer ? (
              <motion.div
                key="revealed"
                initial={{ rotateX: 90, opacity: 0 }}
                animate={{ rotateX: 0, opacity: 1 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
                style={{ perspective: 600 }}
              >
                <p style={styles.answerText}>{partnerEntryToday?.answer}</p>
              </motion.div>
            ) : partnerAnsweredToday ? (
              <motion.div key="locked" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={styles.lockedAnswer}>
                <p style={styles.lockedTitle}>Cevabı hazır.</p>
                <span style={styles.lockedSub}>Seninki kaydolunca açılır.</span>
              </motion.div>
            ) : (
              <motion.p
                key="waiting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={styles.waitingText}
              >
                {partner} henüz yazmadı.
              </motion.p>
            )}
          </AnimatePresence>
        </motion.section>

        <button type="button" style={styles.rhythmCard} onClick={() => navigate('streak')}>
          <div style={styles.rhythmHeader}>
            <div>
              <p style={styles.rhythmKicker}>Ritmimiz</p>
              <strong style={styles.rhythmCount}>{rhythmDisplay}</strong>
            </div>
            <span style={styles.rhythmMark}>›</span>
          </div>
          <p style={styles.rhythmText}>{rhythmText}</p>
          <div style={styles.rhythmDots} aria-hidden="true">
            {recentRhythm.map(item => (
              <span
                key={item.date}
                style={{
                  ...styles.rhythmDot,
                  ...(item.both ? styles.rhythmDotFull : item.one ? styles.rhythmDotHalf : {}),
                  ...(item.date === today ? styles.rhythmDotToday : {}),
                }}
              />
            ))}
          </div>
        </button>

        {/* Past days */}
        {pastDays.length > 0 && (
          <div style={styles.historyBlock}>
            <p style={styles.sectionTitle}>Daha Önce</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {pastDays.map(day => {
                const myEntry = day.answers[user.uid];
                const partnerEntry = Object.entries(day.answers).find(([uid]) => uid !== user.uid);
                return (
                  <div key={day.date} style={styles.pastCard}>
                    <p style={styles.pastDate}>{formatDate(day.date)}</p>
                    <p style={styles.pastQuestion}>"{day.question}"</p>
                    {myEntry && (
                      <div style={styles.pastAnswer}>
                        <span style={{ color: '#FF6B9D', fontSize: 11, fontWeight: 600 }}>
                          {user.displayName}
                        </span>
                        <p style={styles.pastAnswerText}>{myEntry.answer}</p>
                      </div>
                    )}
                    {partnerEntry && (
                      <div style={styles.pastAnswer}>
                        <span style={{ color: '#C77DFF', fontSize: 11, fontWeight: 600 }}>
                          {partnerEntry[1].name}
                        </span>
                        <p style={styles.pastAnswerText}>{partnerEntry[1].answer}</p>
                      </div>
                    )}
                    {!myEntry && !partnerEntry && (
                      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                        Cevap girilmedi
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Inter',sans-serif", fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  body: { padding: '0 16px 28px', display: 'flex', flexDirection: 'column', gap: 12 },
  breakIcon: {
    width: 28, height: 28, borderRadius: 10, display: 'grid', placeItems: 'center',
    background: 'rgba(239,68,68,0.16)', color: '#FCA5A5', fontWeight: 900,
  },
  breakBanner: {
    margin: '0 16px 4px', padding: '12px 16px',
    background: 'linear-gradient(135deg, rgba(127,29,29,0.34), rgba(15,23,42,0.56))',
    border: '1px solid rgba(239,68,68,0.22)', borderRadius: 18,
    display: 'flex', alignItems: 'center', gap: 12,
  },
  breakTitle: { fontSize: 14, fontWeight: 700, color: '#ff8888', marginBottom: 2 },
  breakSub: { fontSize: 12, color: 'rgba(255,255,255,0.5)' },
  breakClose: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', padding: 0 },
  questionCard: {
    background: 'linear-gradient(145deg, rgba(191,90,242,0.18), rgba(255,255,255,0.045))',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 32,
    padding: '24px 20px 26px',
    display: 'flex', flexDirection: 'column', gap: 12,
    boxShadow: '0 24px 80px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)',
    backdropFilter: 'blur(22px) saturate(180%)',
  },
  questionMeta: { display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', color: 'rgba(255,255,255,0.52)', fontSize: 12, fontWeight: 850 },
  questionText: { fontSize: 27, fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 1.13 },
  rhythmCard: {
    width: '100%',
    border: '1px solid rgba(191,90,242,0.16)',
    background: 'linear-gradient(145deg, rgba(191,90,242,0.13), rgba(255,255,255,0.045))',
    borderRadius: 28,
    padding: '16px 16px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    textAlign: 'left',
    cursor: 'pointer',
    backdropFilter: 'blur(20px) saturate(175%)',
    boxShadow: '0 18px 58px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.07)',
  },
  rhythmHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 14,
  },
  rhythmKicker: {
    color: 'rgba(255,255,255,0.52)',
    fontSize: 12,
    fontWeight: 850,
    marginBottom: 4,
  },
  rhythmCount: {
    color: '#fff',
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: '-0.045em',
    lineHeight: 1,
  },
  rhythmMark: {
    width: 36,
    height: 36,
    borderRadius: 16,
    display: 'grid',
    placeItems: 'center',
    color: '#BF5AF2',
    background: 'rgba(191,90,242,0.13)',
    border: '1px solid rgba(191,90,242,0.2)',
    fontWeight: 900,
  },
  rhythmText: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 14,
    lineHeight: 1.42,
  },
  rhythmDots: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 7,
  },
  rhythmDot: {
    height: 8,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.09)',
    border: '1px solid rgba(255,255,255,0.055)',
  },
  rhythmDotHalf: {
    background: 'rgba(191,90,242,0.28)',
    borderColor: 'rgba(191,90,242,0.18)',
  },
  rhythmDotFull: {
    background: 'linear-gradient(90deg, rgba(191,90,242,0.92), rgba(255,255,255,0.68))',
    borderColor: 'rgba(255,255,255,0.12)',
  },
  rhythmDotToday: {
    boxShadow: '0 0 0 2px rgba(191,90,242,0.22)',
  },
  composerCard: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 28,
    padding: 16,
    backdropFilter: 'blur(20px) saturate(180%)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  answerCard: {
    background: 'rgba(255,255,255,0.055)',
    border: '1px solid rgba(255,255,255,0.085)',
    borderRadius: 28,
    padding: '16px 17px',
    backdropFilter: 'blur(20px) saturate(180%)',
  },
  partnerAnswerCard: {
    background: 'linear-gradient(145deg, rgba(191,90,242,0.11), rgba(255,255,255,0.055))',
    border: '1px solid rgba(191,90,242,0.16)',
    borderRadius: 28,
    padding: '17px 18px',
    backdropFilter: 'blur(20px) saturate(180%)',
    boxShadow: '0 18px 58px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.07)',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 },
  cardLabel: { fontSize: 13, fontWeight: 850, color: 'rgba(255,255,255,0.68)', margin: 0 },
  answerText: { fontSize: 15, lineHeight: 1.58, color: 'rgba(255,255,255,0.84)' },
  textarea: { minHeight: 142, borderRadius: 20 },
  lockedAnswer: {
    borderRadius: 20,
    padding: '15px 16px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
  },
  lockedTitle: { color: 'rgba(255,255,255,0.76)', fontSize: 14, fontWeight: 800, marginBottom: 4 },
  lockedSub: { color: 'rgba(255,255,255,0.42)', fontSize: 13, lineHeight: 1.45 },
  waitingText: { color: 'rgba(255,255,255,0.38)', fontSize: 14, lineHeight: 1.5 },
  editBtn: { fontSize: 12, color: 'rgba(191,90,242,0.88)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 850 },
  historyBlock: { marginTop: 8 },
  sectionTitle: { fontSize: 12, fontWeight: 850, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, paddingLeft: 4, marginBottom: 10 },
  pastCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.075)',
    borderRadius: 22,
    padding: '15px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  pastDate: { fontSize: 11, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 0.5 },
  pastQuestion: { fontSize: 14, fontWeight: 650, color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 },
  pastAnswer: { display: 'flex', flexDirection: 'column', gap: 3 },
  pastAnswerText: { fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' },
};
